import Api from "../services/AxiosService";
import { unwrap } from "./envelope";
import type { AuthUser } from "../types/auth-types/AuthUser";

/** Wire shape from svc-03 / shared-types UserAssumptionsSchema */
export type UserAssumptionsDto = {
  laborRate?: string;
  daysPerYear?: string;
  hoursPerDay?: string;
  scrapManual?: string;
  scrapAuto?: string;
  reworkCost?: string;
  oeeManual?: string;
  oeeAuto?: string;
  usefulLifeYears?: string;
};

export type MeResponse = {
  user: AuthUser;
  assumptions: UserAssumptionsDto | null;
};

export type WorkForCandidate = {
  userId: string | null;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  isRegistered: boolean;
};

export type WorkForCustomer = {
  kind: "self" | "registered" | "other";
  userId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
};

export type WorkForState = {
  salesUserId: string;
  effective: WorkForCustomer;
};

export async function fetchMe(): Promise<MeResponse> {
  // forwardUserToken: the shared demographic subset is overlaid from the central
  // directory (svc-03 relays the issuer id_token to the registration-api).
  const res = await Api.get("/v1/users/me", { forwardUserToken: true });
  return unwrap<MeResponse>(res.data);
}

export type UpdateMyProfileInput = {
  firstName?: string;
  lastName?: string;
  phone?: string | null;
  companyName?: string | null;
  companyAddress?: string | null;
  departmentId?: number | null;
};

export async function updateMyProfile(body: UpdateMyProfileInput): Promise<AuthUser> {
  // forwardUserToken: shared-field edits are written through to the central
  // directory before the local write; without the token the shared edit is refused.
  const res = await Api.patch("/v1/users/me", body, { forwardUserToken: true });
  return unwrap<AuthUser>(res.data);
}

export async function putMyAssumptions(
  body: UserAssumptionsDto,
): Promise<UserAssumptionsDto> {
  const res = await Api.put("/v1/users/me/assumptions", body);
  return unwrap<UserAssumptionsDto>(res.data);
}

export async function postWorkFor(body: {
  userId?: string;
  email?: string;
}): Promise<WorkForState> {
  const res = await Api.post("/v1/users/me/work-for", body);
  return unwrap<WorkForState>(res.data);
}

export async function fetchWorkForCandidates(
  region?: string,
): Promise<WorkForCandidate[]> {
  const res = await Api.get("/v1/users/work-for-candidates", {
    params: region ? { region } : undefined,
  });
  return unwrap<{ items: WorkForCandidate[] }>(res.data).items;
}
